import { useMutation } from '@tanstack/react-query';
import { motion } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { create } from 'zustand';

import { type RegistrationCredential, webauthnBeginRegistration, webauthnFinishRegistration } from '@column/api';
import { Icon as KitIcon } from '@column/column-ui-kit';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  Icon,
  Input,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@column/ui';

import { FormField, NotificationList } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { createPublicKeyCredential, getDefaultPasskeyName, isWebAuthnSupported } from '~/lib/webauthn';
import { FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { formatDateTime } from '~/util';

import { DialogHeaderWithClose } from './_components/DialogHeaderWithClose';

const ICON_CLASS_NAME = '[--icon-size-x:16px] [--icon-size-y:16px]';

const CARD_TRANSITION = { duration: 0.22, ease: 'easeOut' } as const;
const ROW_TRANSITION = { duration: 0.24, ease: 'easeOut' } as const;

interface PendingRegistration {
  challengeId: string;
  credential: RegistrationCredential;
}

interface NamePasskeyForm {
  displayName: string;
}

// Shared so the header button (a separate subtree) can hide the moment the last passkey is being
// removed, instead of waiting for the refetch to empty the session store.
const useRemovingLastPasskey = create<{ removingLast: boolean; setRemovingLast: (removingLast: boolean) => void }>(
  (set) => ({
    removingLast: false,
    setRemovingLast: (removingLast) => set({ removingLast }),
  })
);

export const AddPasskeyButton: React.FC<{ className?: string; hideWhenEmpty?: boolean; label?: string }> = ({
  className,
  hideWhenEmpty,
  label = 'Create Passkey',
}) => {
  const { addDangerNotification, addSuccessNotification, clearNotifications } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });
  const hasPasskeys = useSessionStore((state) =>
    (state.currentUser?.factors ?? []).some((factor) => factor.factorType === FactorTypeResponse.WEBAUTHN)
  );
  const removingLast = useRemovingLastPasskey((state) => state.removingLast);
  const firstName = useSessionStore((state) => state.currentUser?.firstName ?? '');
  const refetchCurrentUser = useSessionStore((state) => state.refetchCurrentUser);
  const isSupported = isWebAuthnSupported();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState<PendingRegistration>();

  const formMethods = useForm<NamePasskeyForm>({ defaultValues: { displayName: '' } });

  const closeDialog = () => {
    setIsOpen(false);
    setPendingRegistration(undefined);
  };

  const beginPasskeyRegistrationMutation = useMutation({
    mutationFn: async (): Promise<PendingRegistration> => {
      const { data: registration } = await webauthnBeginRegistration({ throwOnError: true });
      const credential = await createPublicKeyCredential(registration.options);

      return {
        challengeId: registration.challenge_id,
        credential,
      };
    },
    onSuccess: (registration) => {
      setPendingRegistration(registration);
      formMethods.reset({ displayName: getDefaultPasskeyName(firstName) });
      clearNotifications('popup');
      setIsOpen(true);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }
      // The browser throws InvalidStateError when the authenticator is already in excludeCredentials.
      if (error instanceof DOMException && error.name === 'InvalidStateError') {
        addDangerNotification({ content: 'A passkey has already been registered to this device' });
        return;
      }
      const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const finishPasskeyRegistrationMutation = useMutation({
    mutationFn: async ({ name, registration }: { name: string; registration: PendingRegistration }) => {
      await webauthnFinishRegistration({
        body: {
          challenge_id: registration.challengeId,
          credential: registration.credential,
          display_name: name,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      addSuccessNotification({ content: 'Passkey added' });
      void refetchCurrentUser();
      closeDialog();
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }
      const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
      addDangerNotification({ content, display: 'popup' });
    },
  });

  const isFinishing = finishPasskeyRegistrationMutation.isPending;

  const onSubmit = formMethods.handleSubmit(({ displayName }) => {
    if (pendingRegistration) {
      finishPasskeyRegistrationMutation.mutate({ name: displayName.trim(), registration: pendingRegistration });
    }
  }, handleSubmitErrors);

  const createButton = (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      className={className}
      disabled={!isSupported || beginPasskeyRegistrationMutation.isPending}
      onClick={() => beginPasskeyRegistrationMutation.mutate()}
    >
      {beginPasskeyRegistrationMutation.isPending ? <Spinner /> : <KitIcon.Plus className={ICON_CLASS_NAME} />}
      {label}
    </Button>
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && !isFinishing && closeDialog()}>
        <DialogContent
          overlayScroll
          showCloseButton={false}
          className="gap-3 sm:max-w-md"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement).querySelector('input')?.focus();
          }}
        >
          <DialogHeaderWithClose title="Name Passkey" disabled={isFinishing} />

          <NotificationList display="popup" spacing="bottom" className="!m-0" />

          <FormProvider {...formMethods}>
            <form
              autoComplete="off"
              onSubmit={(event) => {
                event.stopPropagation();
                void onSubmit(event);
              }}
              className="-mx-6 -mt-6 [--page-margin:24px]"
            >
              <div className="grid gap-3 px-6 pt-6 pb-3">
                <DialogDescription>Use a name that helps you recognize this key.</DialogDescription>
                <FormField<NamePasskeyForm>
                  id="displayName"
                  label="Name"
                  noStyle
                  rules={{
                    required: 'Please enter a name for your passkey',
                    validate: (value) => !!String(value).trim() || 'Please enter a name for your passkey',
                  }}
                >
                  {({ value, onChange, onBlur }, { error }) => (
                    <Input
                      placeholder="e.g. Work laptop"
                      aria-label="Passkey name"
                      maxLength={127}
                      aria-invalid={!!error}
                      disabled={isFinishing}
                      value={String(value ?? '')}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  )}
                </FormField>
              </div>
            </form>
          </FormProvider>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="secondary" size="sm" onClick={closeDialog} disabled={isFinishing}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={onSubmit} disabled={!pendingRegistration || isFinishing}>
              <KitIcon.Key className={ICON_CLASS_NAME} />
              {isFinishing ? 'Adding…' : 'Add Passkey'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(!hideWhenEmpty || (hasPasskeys && !removingLast)) &&
        (isSupported ? (
          createButton
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className={cn('inline-flex rounded-lg focus-visible:outline-none', className)}>
                {createButton}
              </span>
            </TooltipTrigger>
            <TooltipContent>Passkeys are not supported by this browser</TooltipContent>
          </Tooltip>
        ))}
    </>
  );
};

export const SectionPasskeys: React.FC = () => {
  const { openAlert } = useDialog();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const currentUser = useSessionStore((state) => state.currentUser);
  const refetchCurrentUser = useSessionStore((state) => state.refetchCurrentUser);
  const passkeys = currentUser?.factors.filter((factor) => factor.factorType === FactorTypeResponse.WEBAUTHN) ?? [];
  const [exitingId, setExitingId] = useState<string | null>(null);
  // When the last passkey is removed the whole card fades/scales out and the empty state fades in.
  // Shared so the header button hides immediately (see useRemovingLastPasskey).
  const cardExiting = useRemovingLastPasskey((state) => state.removingLast);
  const setCardExiting = useRemovingLastPasskey((state) => state.setRemovingLast);
  // Flipped as soon as the card finishes animating out so the empty state fades in immediately,
  // rather than waiting for the refetch to empty the list.
  const [optimisticEmpty, setOptimisticEmpty] = useState(false);

  // Guard against a stale shared flag if we leave mid-animation.
  useEffect(() => () => setCardExiting(false), [setCardExiting]);

  // Track which rows have already been rendered so only freshly-added passkeys animate in
  // (not the ones present on first render), and skip the row animation when the whole card is
  // mounting from the empty state — the card's own fade covers that transition.
  const seenIdsRef = useRef<Set<string>>(new Set(passkeys.map((passkey) => passkey.factorId)));
  const prevCountRef = useRef(passkeys.length);

  useEffect(() => {
    passkeys.forEach((passkey) => seenIdsRef.current.add(passkey.factorId));
    prevCountRef.current = passkeys.length;
  });

  // Once the refetch has actually emptied the list, drop the transient exit flags.
  useEffect(() => {
    if (passkeys.length === 0 && (cardExiting || optimisticEmpty)) {
      setCardExiting(false);
      setOptimisticEmpty(false);
    }
  }, [passkeys.length, cardExiting, optimisticEmpty, setCardExiting]);

  const isEntering = (factorId: string) => prevCountRef.current > 0 && !seenIdsRef.current.has(factorId);

  const deletePasskeyMutation = useMutation({
    mutationFn: ({ factorId }: { factorId: string; isLast: boolean }) => UserRepository.deleteFactor({ factorId }),
    onSuccess: (_, { factorId, isLast }) => {
      if (isLast) {
        setCardExiting(true);
      } else {
        setExitingId(factorId);
      }
      addSuccessNotification({ content: 'Passkey deleted' });
      // Leave the exit flags set so the row/card stays out while the refetch removes it.
      void refetchCurrentUser();
    },
    onError: (error: unknown) => {
      setExitingId(null);
      setCardExiting(false);
      setOptimisticEmpty(false);
      const content = error instanceof Error ? error.message : 'Failed to delete passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const handleDelete = (passkey: { factorId: string; displayName: string }) => {
    openAlert({
      headline: 'Delete passkey?',
      content: `Are you sure you want to delete ${passkey.displayName}? You will no longer be able to use it to sign in.`,
      icon: 'Trash',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        deletePasskeyMutation.mutate({ factorId: passkey.factorId, isLast: passkeys.length === 1 });
      },
    });
  };

  return (
    <div className="@container/passkeys">
      <div className="grid grid-cols-[auto] items-start gap-x-6 gap-y-2 @tablet/passkeys:grid-cols-[minmax(180px,25%)_minmax(0,1fr)] @tablet/passkeys:gap-y-0 @wide/passkeys:grid-cols-[minmax(180px,25%)_auto_25%]">
        <div className="text-sm leading-[18px] font-medium text-foreground @tablet/passkeys:mt-4">Your Passkeys</div>
        {passkeys.length === 0 || optimisticEmpty ? (
          <motion.div
            key="empty"
            data-cy="passkeys-empty"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={CARD_TRANSITION}
            className="@container rounded-lg bg-gray-25 px-4 py-3 ring-1 ring-black/[0.08] dark:bg-white/[0.02] dark:ring-white/[0.12]"
          >
            <div className="flex items-center gap-3 @max-[420px]:flex-col @max-[420px]:items-stretch">
              <div className="flex min-w-0 flex-auto items-center gap-3">
                <Icon.Passkey className="self-start text-gray-400 dark:text-gray-500" />
                <div className="flex min-w-0 flex-auto flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">Create Passkey</span>
                  <span className="text-xs text-muted-foreground">
                    Passkeys are more secure than passwords and the strongest protection against attacks like phishing.
                  </span>
                </div>
              </div>
              <AddPasskeyButton className="@max-[420px]:w-full" label="Create" />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={cardExiting ? { opacity: 0, scale: 0.96 } : { opacity: 1, scale: 1 }}
            transition={CARD_TRANSITION}
            onAnimationComplete={() => {
              if (cardExiting) {
                setOptimisticEmpty(true);
              }
            }}
            className={cn(
              'overflow-hidden rounded-lg bg-background shadow-btn-secondary-flat dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]',
              cardExiting && 'pointer-events-none'
            )}
          >
            <ul className="m-0 flex flex-col p-0">
              {passkeys.map((passkey) => {
                const isExiting = exitingId === passkey.factorId;

                return (
                  <motion.li
                    key={passkey.factorId}
                    data-cy="passkey-row"
                    initial={isEntering(passkey.factorId) ? { height: 0, opacity: 0 } : false}
                    animate={isExiting ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
                    transition={ROW_TRANSITION}
                    className={cn(
                      'list-none overflow-hidden border-t border-gray-100 first:border-t-0 dark:border-white/[0.08]',
                      isExiting && 'pointer-events-none border-transparent'
                    )}
                  >
                    <div className="flex items-center gap-4 px-4 py-3">
                      <div className="grid min-w-0 flex-1 gap-0.5">
                        <span className="truncate text-sm font-medium text-foreground">{passkey.displayName}</span>
                        {passkey.createdAt && (
                          <span className="text-xs text-muted-foreground">
                            Created {formatDateTime(passkey.createdAt, { timeZoneFormat: 'local' })}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="muted"
                        size="icon-sm"
                        aria-label={`Delete ${passkey.displayName}`}
                        disabled={deletePasskeyMutation.isPending}
                        onClick={() => handleDelete(passkey)}
                        className="text-gray-400 shadow-none hover:bg-red-50 hover:text-destructive hover:shadow-none focus-visible:ring-destructive/20 dark:text-gray-500 dark:shadow-none dark:hover:bg-red-950/40"
                      >
                        <KitIcon.Trash className={ICON_CLASS_NAME} />
                      </Button>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
};
